import React, { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Check, Search, ChevronDown, Tag } from 'lucide-react'
import { attributionTagService } from '@/services/attributionTagService'
import useGlobalCustomerStore from '@/stores/globalCustomerStore'
import { clsx } from 'clsx'

// Local type for the picker that includes status
interface PickerTag {
  hashtag_id: number
  hashtag_text: string
  description: string | null
  status: string | null
}

interface TagPickerProps {
  selectedTagIds: number[]
  onChange: (tagIds: number[]) => void
  customerId?: string
  error?: string
  disabled?: boolean
  required?: boolean
  placeholder?: string
  singleSelect?: boolean
  hideLabel?: boolean
}

const TagPicker: React.FC<TagPickerProps> = ({
  selectedTagIds,
  onChange,
  customerId: propCustomerId,
  error,
  disabled = false,
  required = false,
  placeholder = 'Select tags...',
  singleSelect = false,
  hideLabel = false
}) => {
  const [tags, setTags] = useState<PickerTag[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 })
  const buttonRef = useRef<HTMLButtonElement>(null)
  const { selectedCustomer } = useGlobalCustomerStore()

  // Use prop customerId if provided, otherwise fall back to global store
  const effectiveCustomerId = propCustomerId || selectedCustomer?.id

  // Update dropdown position when opened or on scroll/resize
  useEffect(() => {
    if (!isOpen || !buttonRef.current) return

    const updatePosition = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect()
        setDropdownPosition({
          top: rect.bottom + 4,
          left: rect.left,
          width: rect.width,
        })
      }
    }

    updatePosition()

    // Update position on scroll or resize
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)

    return () => {
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [isOpen])

  useEffect(() => {
    const loadTags = async () => {
      if (!effectiveCustomerId) return

      setIsLoading(true)
      try {
        // Use getHashtagsList to get all attribution tags from the new table,
        // then filter to only show active tags in the picker
        const response = await attributionTagService.getHashtagsList(
          String(effectiveCustomerId),
          'attribution'
        )
        // Filter to only include active tags (case-insensitive check)
        const activeTags = (response.data || []).filter(
          (tag) => tag.status?.toLowerCase() === 'active'
        )
        setTags(activeTags)
      } catch (err) {
        console.error('Failed to load tags:', err)
        setTags([])
      } finally {
        setIsLoading(false)
      }
    }

    loadTags()
  }, [effectiveCustomerId])

  const filteredTags = tags.filter(
    (tag) =>
      tag.hashtag_text.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (tag.description &&
        tag.description.toLowerCase().includes(searchQuery.toLowerCase()))
  )

  const toggleTag = (tagId: number) => {
    if (singleSelect) {
      onChange([tagId])
      setIsOpen(false)
    } else {
      if (selectedTagIds.includes(tagId)) {
        onChange(selectedTagIds.filter((id) => id !== tagId))
      } else {
        onChange([...selectedTagIds, tagId])
      }
    }
  }

  const selectedCount = selectedTagIds.length
  const selectedTagNames = tags
    .filter((tag) => selectedTagIds.includes(Number(tag.hashtag_id)))
    .map((tag) => tag.hashtag_text.startsWith('@') ? tag.hashtag_text : `#${tag.hashtag_text}`)
    .join(', ')

  // Render dropdown in a portal to escape overflow containers
  const dropdownContent = isOpen && createPortal(
    <>
      {/* Click outside to close */}
      <div
        className="fixed inset-0 z-[9998]"
        onClick={() => setIsOpen(false)}
      />

      {/* Dropdown Menu */}
      <div
        className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-hidden"
        style={{
          top: dropdownPosition.top,
          left: dropdownPosition.left,
          width: dropdownPosition.width,
        }}
      >
        {/* Search Input */}
        <div className='p-2 border-b border-gray-100'>
          <div className='relative'>
            <Search className='absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400' />
            <input
              type='text'
              placeholder='Search tags...'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className='w-full pl-7 pr-2 py-1 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500'
            />
          </div>
        </div>

        {/* Tag List */}
        <div className='overflow-y-auto max-h-48'>
          {isLoading ? (
            <div className='p-3 text-center text-xs text-gray-500'>
              Loading tags...
            </div>
          ) : filteredTags.length === 0 ? (
            <div className='p-3 text-center text-xs text-gray-500'>
              {searchQuery
                ? 'No tags match your search'
                : 'No active tags available'}
            </div>
          ) : (
            filteredTags.map((tag) => {
              const tagId = Number(tag.hashtag_id)
              const isSelected = selectedTagIds.includes(tagId)
              return (
                <button
                  key={tagId}
                  type='button'
                  onClick={() => toggleTag(tagId)}
                  className={clsx(
                    'w-full flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 transition-colors',
                    isSelected && 'bg-blue-50'
                  )}
                >
                  {/* Checkbox */}
                  <div
                    className={clsx(
                      'h-3.5 w-3.5 rounded border flex items-center justify-center flex-shrink-0',
                      isSelected
                        ? 'bg-blue-600 border-blue-600'
                        : 'border-gray-300'
                    )}
                  >
                    {isSelected && (
                      <Check className='h-2.5 w-2.5 text-white' />
                    )}
                  </div>

                  {/* Tag Icon */}
                  <Tag className='h-3 w-3 text-gray-400 flex-shrink-0' />

                  {/* Tag Info */}
                  <div className='flex-1 text-left min-w-0'>
                    <div className='font-medium text-xs text-gray-900 truncate'>
                      {tag.hashtag_text.startsWith('@') ? tag.hashtag_text : `#${tag.hashtag_text}`}
                    </div>
                    {tag.description && (
                      <div className='text-xs text-gray-500 truncate'>
                        {tag.description}
                      </div>
                    )}
                  </div>
                </button>
              )
            })
          )}
        </div>
      </div>
    </>,
    document.body
  )

  return (
    <div className='space-y-2'>
      {!hideLabel && (
        <label className='block text-xs font-medium text-gray-700'>
          Attribution Tags {required && <span className='text-red-500'>*</span>}
        </label>
      )}

      {/* Dropdown Button */}
      <div className='relative'>
        <button
          ref={buttonRef}
          type='button'
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={clsx(
            'w-full flex items-center justify-between px-2 py-1.5 border rounded-lg text-left transition-colors',
            disabled
              ? 'bg-gray-100 cursor-not-allowed'
              : 'bg-white hover:border-gray-400 cursor-pointer',
            error ? 'border-red-500' : 'border-gray-300'
          )}
        >
          <span
            className={clsx(
              'text-xs truncate',
              selectedCount === 0 ? 'text-gray-500' : 'text-gray-900'
            )}
          >
            {selectedCount === 0
              ? placeholder
              : singleSelect
              ? selectedTagNames
              : `${selectedCount} tag${
                  selectedCount !== 1 ? 's' : ''
                } selected`}
          </span>
          <ChevronDown
            className={clsx(
              'h-3.5 w-3.5 text-gray-400 transition-transform flex-shrink-0',
              isOpen && 'rotate-180'
            )}
          />
        </button>
      </div>

      {/* Portal-rendered dropdown */}
      {dropdownContent}

      {/* Error Message */}
      {error && <p className='text-xs text-red-500'>{error}</p>}

      {/* Helper Text */}
      {required && selectedTagIds.length === 0 && !error && (
        <p className='text-xs text-gray-500'>
          {singleSelect ? 'Select a tag' : 'At least one tag is required'}
        </p>
      )}
    </div>
  )
}

export default TagPicker
